import Box from '@mui/material/Box';
import { isArray } from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkProps } from 'react-router-dom';

import { UnitSearchResult } from 'api';
import { Info } from 'assets/volvoIcons/Informational/Info';
import { DialogIconButton } from 'library/components/controls/buttons/DialogIconButton';
import { useAdobeTracking } from 'library/hooks/useAdobeTracking';
import { Sxable } from 'library/types';
import {
	UnitDetailView,
	UnitDetailViewData,
} from 'modules/MaintenancePlanner/Components/UnitDetailView';
import { UnitTitle, UnitTitleProps } from 'modules/SearchProduct/UnitTitle';
import AvatarWithCampaign from 'modules/Shared/Components/ConnectedUnits/AvatarWithCampaign';
import { UnitOverviewLink } from 'modules/Shared/Components/ConnectedUnits/UnitOverviewLink';

export type UnitCellProps = Omit<
	UnitSearchResult,
	| 'city'
	| 'country'
	| 'ownerCompanyName'
	| 'ownerName'
	| 'ownerId'
	| 'ownerSurname'
	| 'ownerEmail'
	| 'isOwnerAnonymous'
	| 'installations'
	| 'serviceAgreementEndDate'
> & {
	linkTarget?: LinkProps['target'];
} & Pick<UnitTitleProps, 'highlightedText'> &
	UnitDetailViewData &
	Sxable;

export const UnitCell = (props: UnitCellProps): React.JSX.Element | null => {
	const {
		id,
		publicId,
		profileImageId,
		outstandingCampaignType,
		queryAsExternal = false,
		linkTarget,
		highlightedText,
		sx = [],
	} = props;

	const { t } = useTranslation(['unitOverview']);
	const { trackApplicationInteraction } = useAdobeTracking();

	const handleOpenUnitDetails = useCallback(() => {
		void trackApplicationInteraction({
			action: 'click',
			stepName: 'MP Open Unit Details',
			selectedOptions: [
				{ attribute: 'unitId', value: id.toString() },
				{ attribute: 'publicId', value: publicId },
			],
		});
	}, [id, publicId, trackApplicationInteraction]);

	const handleUnitLinkClick = useCallback(() => {
		void trackApplicationInteraction({
			action: 'click',
			stepName: 'MP Unit Name Link',
			selectedOptions: [
				{ attribute: 'unitId', value: id.toString() },
				{ attribute: 'publicId', value: publicId },
			],
		});
	}, [id, publicId, trackApplicationInteraction]);

	return id > 0 ? (
		<Box
			sx={[
				{
					display: 'grid',
					gridTemplateColumns:
						'max-content minmax(50px, 1fr) max-content',
					alignSelf: 'stretch',
					alignItems: 'start',
					flex: 1,
					gap: 2,
					py: 1,
				},
				...(isArray(sx) ? sx : [sx]),
			]}>
			<UnitOverviewLink
				unitPublicId={publicId}
				target={linkTarget}
				onClick={handleUnitLinkClick}
				sx={{
					display: 'contents',

					':hover': {
						'& p': {
							textDecoration: 'underline',
						},
					},
				}}>
				<AvatarWithCampaign
					imageId={profileImageId}
					campaignType={outstandingCampaignType}
					withZoom
				/>

				<UnitTitle {...props} highlightedText={highlightedText} />
			</UnitOverviewLink>

			<DialogIconButton
				DialogComponent={UnitDetailView}
				dialogProps={{ data: { id, queryAsExternal } }}
				tooltipText={t('unitOverview:unit-detailed-button-tooltip')}
				Icon={<Info color="secondary" />}
				onOpen={handleOpenUnitDetails}
			/>
		</Box>
	) : null;
};
